/**
 * A four-function calculator, as a pure state machine.
 *
 * It exists because exam simulators have one and some exams need it — and because the alternative,
 * a candidate reaching for their phone mid-question, is worse for them than an on-screen keypad.
 *
 * Modelled as data and a `press` function rather than as component state, for the same reason the
 * session is: arithmetic that decides what a person writes down is worth testing, and none of
 * these rules are testable through a rendered keypad. There is no expression parser and no `eval`
 * anywhere near it — an operator is applied when the next one arrives, which is exactly what the
 * hardware it imitates does.
 */

export type Operator = '+' | '-' | '*' | '/';

export type CalculatorKey =
  | { readonly kind: 'digit'; readonly value: string }
  | { readonly kind: 'decimal' }
  | { readonly kind: 'operator'; readonly value: Operator }
  | { readonly kind: 'equals' }
  | { readonly kind: 'clear' }
  | { readonly kind: 'clear-entry' }
  | { readonly kind: 'backspace' }
  | { readonly kind: 'negate' }
  | { readonly kind: 'percent' };

export interface CalculatorState {
  /** Always a number, never a word: an error is a flag, so the message stays translatable. */
  readonly display: string;
  readonly accumulator: number | null;
  readonly pending: Operator | null;
  /** True when the next digit starts a new number rather than extending the shown one. */
  readonly replacing: boolean;
  readonly error: boolean;
}

export const INITIAL: CalculatorState = {
  display: '0',
  accumulator: null,
  pending: null,
  replacing: true,
  error: false,
};

const MAX_DIGITS = 15;

export function press(state: CalculatorState, key: CalculatorKey): CalculatorState {
  // Any key after an error clears it, except the ones that would carry the error forward.
  if (state.error && key.kind !== 'clear') {
    return key.kind === 'clear-entry' ? INITIAL : state;
  }

  switch (key.kind) {
    case 'digit':
      return typed(state, key.value);

    case 'decimal':
      if (state.replacing) {
        return { ...state, display: '0.', replacing: false };
      }
      return state.display.includes('.') ? state : { ...state, display: `${state.display}.` };

    case 'operator':
      return operator(state, key.value);

    case 'equals':
      return equals(state);

    case 'clear':
      return INITIAL;

    case 'clear-entry':
      return { ...state, display: '0', replacing: true };

    case 'backspace': {
      if (state.replacing) {
        return state;
      }
      const shorter = state.display.slice(0, -1);
      return {
        ...state,
        display: shorter === '' || shorter === '-' ? '0' : shorter,
        replacing: shorter === '' || shorter === '-',
      };
    }

    case 'negate': {
      if (state.display === '0') {
        return state;
      }
      return {
        ...state,
        display: state.display.startsWith('-') ? state.display.slice(1) : `-${state.display}`,
      };
    }

    case 'percent': {
      // Per cent of the number being operated on, as a desk calculator does it: 200 + 10 % is 220,
      // not 200.1. With nothing pending it is simply a division by a hundred.
      const current = value(state);
      const base = state.accumulator ?? 0;
      const result =
        state.pending === '+' || state.pending === '-' ? (base * current) / 100 : current / 100;
      return { ...state, display: format(result), replacing: true };
    }
  }
}

function typed(state: CalculatorState, digit: string): CalculatorState {
  if (state.replacing) {
    return { ...state, display: digit, replacing: false };
  }
  // A cap rather than an overflow: beyond this the underlying double stops being exact, and a
  // calculator that quietly shows an inexact total is worse than one that stops accepting digits.
  const digitsSoFar = state.display.replace(/[-.]/g, '').length;
  if (digitsSoFar >= MAX_DIGITS) {
    return state;
  }
  return { ...state, display: state.display === '0' ? digit : `${state.display}${digit}` };
}

function operator(state: CalculatorState, next: Operator): CalculatorState {
  // Pressing two operators in a row changes the pending one instead of applying anything: nothing
  // new has been typed, so there is nothing to apply it to.
  if (state.replacing && state.accumulator !== null && state.pending !== null) {
    return { ...state, pending: next };
  }

  const current = value(state);
  const folded = state.pending === null || state.accumulator === null
    ? current
    : apply(state.accumulator, current, state.pending);

  if (folded === null) {
    return { ...INITIAL, error: true, display: '0' };
  }

  return {
    ...state,
    display: format(folded),
    accumulator: folded,
    pending: next,
    replacing: true,
  };
}

function equals(state: CalculatorState): CalculatorState {
  if (state.pending === null || state.accumulator === null) {
    return { ...state, replacing: true };
  }

  const result = apply(state.accumulator, value(state), state.pending);
  if (result === null) {
    return { ...INITIAL, error: true, display: '0' };
  }

  return { ...state, display: format(result), accumulator: null, pending: null, replacing: true };
}

/** Null means the operation has no answer — division by zero, and nothing else. */
function apply(left: number, right: number, op: Operator): number | null {
  switch (op) {
    case '+':
      return left + right;
    case '-':
      return left - right;
    case '*':
      return left * right;
    case '/':
      return right === 0 ? null : left / right;
  }
}

function value(state: CalculatorState): number {
  const parsed = Number(state.display);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * A number as a calculator would show it.
 *
 * Rounded to twelve significant figures before display, which is what stops `0.1 + 0.2` from
 * appearing as `0.30000000000000004` — the single most common way a correct calculation looks
 * broken.
 */
export function format(result: number): string {
  if (!Number.isFinite(result)) {
    return '0';
  }
  if (Math.abs(result) >= 1e15 || (result !== 0 && Math.abs(result) < 1e-9)) {
    return result.toExponential(6).replace(/e([+-])(\d)$/, 'e$10$2');
  }
  return String(Number(result.toPrecision(12)));
}
